import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Settings, ShieldAlert, Check } from "lucide-react";
import {
  getCookieParts,
  saveCookieParts,
  hasCookies,
} from "../lib/cookies";
import { toast } from "sonner";

export const SettingsButton = ({ onChange }) => {
  const [open, setOpen] = useState(false);
  const [parts, setParts] = useState({ sessionid: "", ds_user_id: "", csrftoken: "" });
  const [hasSaved, setHasSaved] = useState(hasCookies());

  useEffect(() => {
    if (open) setParts(getCookieParts());
  }, [open]);

  const upd = (k, v) => setParts((p) => ({ ...p, [k]: v }));

  const save = () => {
    const combined = saveCookieParts(parts);
    setHasSaved(!!combined);
    onChange?.();
    toast.success(combined ? "Cookies сохранены" : "Cookies очищены");
    setOpen(false);
  };

  const clearAll = () => {
    setParts({ sessionid: "", ds_user_id: "", csrftoken: "" });
  };

  const field = (name, label, testId, required = false) => (
    <div className="space-y-1.5">
      <Label className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">
        <span className="mono">{label}</span>
        {required && <span className="text-pink-500">*</span>}
      </Label>
      <Input
        data-testid={testId}
        value={parts[name]}
        onChange={(e) => upd(name, e.target.value)}
        placeholder={required ? "обязательно" : "необязательно"}
        spellCheck={false}
        autoComplete="off"
        className="bg-black/60 border-white/10 focus-visible:ring-1 focus-visible:ring-pink-600/60 focus-visible:border-pink-600/50 text-zinc-100 placeholder:text-zinc-600 mono text-xs h-10"
      />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          data-testid="settings-button"
          className="relative inline-flex items-center gap-2 border border-white/10 hover:border-white/25 rounded-full px-3 py-1.5 text-xs text-zinc-400 hover:text-white transition-colors duration-200"
        >
          <Settings className="w-3.5 h-3.5" />
          Cookies
          {hasSaved && (
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.7)]" />
          )}
        </button>
      </DialogTrigger>

      <DialogContent className="bg-[#0a0a0b] border-white/10 text-zinc-100 max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl tracking-tight">
            Instagram cookies
          </DialogTitle>
          <DialogDescription className="text-zinc-400 text-sm leading-relaxed pt-1">
            Вставь <b className="text-zinc-200">только значения</b> — по одному в поле. Без имён и знаков «=».
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-xl border border-white/10 bg-black/40 p-4">
          <p className="text-xs text-zinc-500 mb-2 flex items-center gap-2">
            <ShieldAlert className="w-3.5 h-3.5 text-amber-500" />
            Cookies лежат <b className="text-zinc-300">только у тебя в браузере</b>.
          </p>
          <ol className="text-xs text-zinc-400 space-y-1 leading-relaxed list-decimal pl-4">
            <li>Открой instagram.com и залогинься.</li>
            <li>F12 → <b className="text-zinc-200">Application</b> → Cookies → <span className="mono">www.instagram.com</span></li>
            <li>Кликай по нужному cookie → в правом окне копируй значение из поля <b className="text-zinc-200">Value</b></li>
            <li>Вставляй сюда в соответствующее поле</li>
          </ol>
        </div>

        <div className="grid grid-cols-1 gap-3">
          {field("sessionid", "sessionid", "cookie-sessionid", true)}
          <div className="grid grid-cols-2 gap-3">
            {field("ds_user_id", "ds_user_id", "cookie-ds-user-id")}
            {field("csrftoken", "csrftoken", "cookie-csrftoken")}
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 pt-1">
          <button
            data-testid="clear-cookies-button"
            onClick={clearAll}
            className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors duration-200"
          >
            Очистить поля
          </button>
          <button
            data-testid="save-cookies-button"
            onClick={save}
            className="ig-gradient text-white font-semibold rounded-xl px-5 py-2.5 text-sm hover:scale-[1.02] active:scale-[0.98] transition-transform duration-200 inline-flex items-center gap-2"
          >
            <Check className="w-4 h-4" />
            Сохранить
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
